import React, { useContext, useState } from 'react';
import { AddressSelectionContext } from './AddressSelectionProvider';
import { AddressInput } from './components/address-input';
import { AddressBookInput } from './components/address-book/AddressBookInput';
import { MobileAddressBook, DesktopAddressBook } from './components/address-book';
import { Modal, ModalContents, ModalOpenButton } from '../modal';
import { CreateAddress } from './components/address-modal/CreateAddress';
import { useMediaQuery } from 'react-responsive';
import { buttonType } from '../button';
import { AnimatePresence, motion } from 'framer-motion';
import { SteppedContainer } from '../container/SteppedContainer';
import { OrderType } from '../../types';
import { mediaQuery } from '../../theme/breakpoints';
import { useUser } from '../../hooks/useUser';
import styled from 'styled-components';
import {
  CreateUserAddressRequest,
  UserAddressDto,
} from '@kotipizzagroup/kotipizza-users-api-client';

export const DeliveryAddressSelection: React.FC = () => {
  const [showAddressBook, setShowAddressBook] = useState(false);

  const {
    orderType,
    inputAddress,
    setAddressBookAddressId,
    setApartmentNumber,
    setInputAddress,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  } = useContext(AddressSelectionContext);

  const isMobile = useMediaQuery({ query: mediaQuery.medium });

  const handleDesktopCreateAddress = async (
    address: CreateUserAddressRequest
  ): Promise<UserAddressDto> => {
    const newAddress = await createAddress(address);
    setShowAddressBook(false);
    return newAddress;
  };

  const { user, isCCUser } = useUser();

  return (
    <StyledSteppedContainer
      data-testid="delivery-address-selection"
      primary
      stepNumber={2}
      title={getTitle(orderType, Boolean(user))}
      fullBottomBorder={true}
      data-clarity-mask="true"
    >
      {!user || isCCUser ? (
        <>
          <AddressInput
            type="secondary"
            placeholder="Esim. Mariankatu 4"
            onAddressSelect={(address: UserAddressDto | undefined | null = null) => {
              setInputAddress(
                (prevAddress) =>
                  ({
                    ...address,
                    apartmentNumber: prevAddress?.apartmentNumber,
                  }) as UserAddressDto
              );
            }}
            onApartmentNumberChange={(e) => setApartmentNumber(e.target.value)}
            preselectAddress={
              inputAddress?.streetAddress
                ? `${inputAddress?.streetAddress}, ${inputAddress?.city}`
                : ''
            }
            preselectApartmentNumber={inputAddress?.apartmentNumber || undefined}
          />
        </>
      ) : user && user.addresses.length === 0 ? ( // no address yet
        <Modal name="AddressSelectionModal">
          <ModalOpenButton type={buttonType.INVERTED}>+ Lisää osoite</ModalOpenButton>
          <ModalContents
            maxWidth="sm"
            fullWidth={true}
            title="Lisää kuljetusosoite"
            aria-labelledby="create-address-modal"
          >
            <CreateAddress
              createAddress={handleDesktopCreateAddress}
              setDefaultAddress={setDefaultAddress}
            />
          </ModalContents>
        </Modal>
      ) : isMobile ? (
        <Modal name="AddressSelectionModal">
          <ModalOpenButton noStyle>
            <AddressBookInput />
          </ModalOpenButton>
          <ModalContents
            maxWidth="sm"
            fullWidth={true}
            title="Valitse kuljetusosoite"
            aria-labelledby="address-book"
            aria-describedby="address-book"
          >
            <MobileAddressBook
              createAddress={createAddress}
              updateAddress={updateAddress}
              deleteAddress={deleteAddress}
              setDefaultAddress={setDefaultAddress}
              onAddressSelect={(address: UserAddressDto | undefined | null = null) => {
                setAddressBookAddressId(address?.addressId || null);
              }}
            />
          </ModalContents>
        </Modal>
      ) : (
        <AnimatePresence>
          {showAddressBook ? (
            <motion.div
              key="address-book"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={{ duration: 0.2 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <DesktopAddressBook
                createAddress={handleDesktopCreateAddress}
                updateAddress={updateAddress}
                deleteAddress={deleteAddress}
                setDefaultAddress={setDefaultAddress}
                onAddressSelect={(address: UserAddressDto | undefined | null = null) => {
                  setAddressBookAddressId(address?.addressId || null);
                  setShowAddressBook(false);
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="address-book-input"
              initial={{ height: 'auto', opacity: 1 }}
              transition={{ duration: 0.2 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <AddressBookInputButton onClick={() => setShowAddressBook(true)}>
                <AddressBookInput />
              </AddressBookInputButton>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </StyledSteppedContainer>
  );
};

export const StyledSteppedContainer = styled(SteppedContainer)`
  &.primary {
    padding-bottom: 2px;
  }
`;

const AddressBookInputButton = styled.button`
  all: unset;
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  width: 100%;

  &:hover,
  &:focus {
    outline: 2px solid black;
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
`;

const getTitle = (orderType: OrderType, hasUser: boolean): string => {
  if (orderType === OrderType.DELIVERY && hasUser) {
    return 'Valitse kuljetusosoite';
  } else if (orderType === OrderType.DELIVERY && !hasUser) {
    return 'Syötä osoite';
  } else {
    return 'Hae ravintoloita';
  }
};
